import React, { lazy, useCallback, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from "react-router-dom";
import AccessibleNavigationAnnouncer from "./components/AccessibleNavigationAnnouncer";
import { useAuth } from "./context/AuthContext";
import { connectWebSocket, disconnectWebSocket } from "./services/websocket";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";


const Layout = lazy(() => import("./containers/Layout"));
const Login = lazy(() => import("./pages/Login"));
const CreateAccount = lazy(() => import("./pages/CreateAccount"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));

function PrivateRoute({ component: Component, ...rest }) {
  const { isAuthenticated } = useAuth();
  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated ? (
          <Component {...props} />
        ) : (
          <Redirect
            to={{
              pathname: "/login",
              state: { from: props.location },
            }}
          />
        )
      }
    />
  );
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <>
      <AccessibleNavigationAnnouncer />
      <Switch>
        <Route
          path="/login"
          render={() =>
            isAuthenticated ? <Redirect to="/dashboard" /> : <Login />
          }
        />
        <Route path="/create-account" component={CreateAccount} />
        <Route path="/forgot-password" component={ForgotPassword} />

        {/* Place new routes over this */}
        <PrivateRoute component={Layout} />

        <Route exact path="/">
          <Redirect to={isAuthenticated ? "/dashboard" : "/login"} />
        </Route>
      </Switch>
    </>
  );
}
function App() {
  const { isAuthenticated } = useAuth();
  const notificationSoundRef = useRef(null);
  const soundUnlockedRef = useRef(false);

  const attemptUnlockSound = useCallback(() => {
    const sound = notificationSoundRef.current;
    if (!sound) return;
    sound
      .play()
      .then(() => {
        sound.pause();
        sound.currentTime = 0;
        soundUnlockedRef.current = true;
        document.removeEventListener("click", attemptUnlockSound);
        document.removeEventListener("keydown", attemptUnlockSound);
      })
      .catch(() => {
        // keep waiting for another interaction
      });
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    if (!notificationSoundRef.current) {
      const sound = new Audio("/notification.mp3");
      sound.volume = 0.6;
      notificationSoundRef.current = sound;
      sound
        .play()
        .then(() => {
          sound.pause();
          sound.currentTime = 0;
          soundUnlockedRef.current = true;
        })
        .catch(() => {
          document.addEventListener("click", attemptUnlockSound);
          document.addEventListener("keydown", attemptUnlockSound);
        });
    }

    connectWebSocket((order) => {
      console.log("New order received:", order);

      // 🔔 play sound
      const sound = notificationSoundRef.current;
      if (sound) {
        sound.currentTime = 0;
        const promise = sound.play();
        if (promise) {
          promise.catch(() => {});
        }
      }

      // 🔔 toast
      toast.info(`New order from ${order.customerName}`, {
        position: "top-right",
        autoClose: 5000,
      });
    });

    return () => disconnectWebSocket();
  }, [isAuthenticated, attemptUnlockSound]);

  return (
    <Router>
      <ToastContainer />
      <AppRoutes />
    </Router>
  );
}

export default App;
